import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
})

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface SignedUrlRequest {
  fileName: string
  fileType: string
  goalEventId: string
  fileSize: number
}

export interface SignedUrlResponse {
  signedUrl: string
  s3Key: string
}

/**
 * Get a signed URL from backend to upload directly to S3
 */
export const getSignedUploadUrl = async (
  request: SignedUrlRequest,
): Promise<SignedUrlResponse> => {
  const response = await api.post('/work-samples/signed-url', request)
  return response.data.data
}

/**
 * Get a signed URL for downloading a work sample from S3
 */
export const getSignedDownloadUrl = async (s3Key: string): Promise<string> => {
  const response = await api.post('/work-samples/signed-download-url', {
    s3Key,
  })
  return response.data.data.signedUrl
}

export const deleteWorkSample = async (
  s3Key: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete('/work-samples', {
    data: { s3Key },
  })

  return response.data
}

/**
 * Upload file directly to S3 using signed URL
 */
export const uploadToS3 = async (
  signedUrl: string,
  file: File,
): Promise<void> => {
  console.log('Uploading to S3:', {
    signedUrl,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
  })

  const response = await fetch(signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  })

  // Log response details for debugging
  const responseText = await response.clone().text()
  console.log('S3 response:', {
    status: response.status,
    statusText: response.statusText,
    body: responseText,
    headers: Object.fromEntries(response.headers),
  })

  if (!response.ok) {
    throw new Error(
      `S3 upload failed: ${response.status} ${response.statusText}. ${responseText}`,
    )
  }
}
